import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CatalogFeature } from "@/types/catalog-feature";
import { useDeleteFeature, useFeatures } from "../hooks/useFeatures";
import { FeatureEditDialog } from "./FeatureEditDialog";
import { CreateFeatureDialog } from "./CreateFeatureDialog";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";

function formatJsonCell(value: unknown): string {
  if (value === null) {
    return "—";
  }
  if (typeof value === "object" && value !== null) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return "—";
    }
    return JSON.stringify(value);
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return "—";
}

interface FeaturesTabProps {
  enabled: boolean;
}

export const FeaturesTab: React.FC<FeaturesTabProps> = ({ enabled }) => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("subscriptions");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<CatalogFeature | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<CatalogFeature | null>(null);

  const { data, isLoading } = useFeatures(enabled);
  const { mutateAsync: deleteFeature, isPending: isDeleting } =
    useDeleteFeature();

  const rows = data ?? [];

  const handleDeleteFeature = async () => {
    if (deleteTarget === null) {
      return;
    }

    try {
      await deleteFeature(deleteTarget.id);
      toast.success(t("subscriptions.features.delete.successToast"));
      setDeleteTarget(null);
    } catch {
      toast.error(t("subscriptions.features.delete.errorToast"));
    }
  };

  const columns: Column<CatalogFeature>[] = useMemo(
    () => [
      {
        key: "title",
        header: t("subscriptions.features.table.title"),
      },
      {
        key: "description",
        header: t("subscriptions.features.table.description"),
        render: (_: unknown, row: CatalogFeature) => (
          <span className="line-clamp-2 max-w-[200px] text-sm text-zinc-600">
            {formatJsonCell(row.description)}
          </span>
        ),
      },
      {
        key: "limit",
        header: t("subscriptions.features.table.limit"),
        className: "text-center text-sm",
        render: (_: unknown, row: CatalogFeature) => formatJsonCell(row.limit),
      },
      {
        key: "unitPrice",
        header: t("subscriptions.features.table.unitPrice"),
        className: "text-center text-sm",
        render: (_: unknown, row: CatalogFeature) =>
          formatJsonCell(row.unitPrice),
      },
      {
        key: "id",
        header: t("subscriptions.features.table.actions"),
        className: "text-right w-24",
        render: (_: string, row: CatalogFeature) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={!canWrite}
              title={t("subscriptions.features.edit.action")}
              aria-label={t("subscriptions.features.edit.action")}
              onClick={(e) => {
                e.stopPropagation();
                setEditingFeature(row);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={!canWrite}
              title={t("subscriptions.features.delete.action")}
              aria-label={t("subscriptions.features.delete.action")}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t, canWrite],
  );

  return (
    <>
      <div className="mb-6 flex justify-end">
        <Button
          type="button"
          disabled={!canWrite}
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          {t("subscriptions.features.create.action")}
        </Button>
      </div>
      <DataTable<CatalogFeature>
        data={rows}
        columns={columns}
        loading={isLoading}
        emptyMessage={t("subscriptions.features.table.empty")}
        withPagination={false}
        withSorting={false}
      />
      <CreateFeatureDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <FeatureEditDialog
        feature={editingFeature}
        onClose={() => setEditingFeature(null)}
      />
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isDeleting) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("subscriptions.features.delete.title")}
            </DialogTitle>
            <DialogDescription>
              {t("subscriptions.features.delete.description", {
                featureTitle: deleteTarget?.title ?? "—",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isDeleting}
              onClick={() => setDeleteTarget(null)}
            >
              {t("subscriptions.features.delete.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting || deleteTarget === null}
              onClick={() => void handleDeleteFeature()}
            >
              {isDeleting ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              {t("subscriptions.features.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
